import { type RequirementAttributes } from './generated/RequirementAttributes';
import ApplicationModel, { attr } from '../ApplicationModel';
import { model } from '../modelBuilder';
import { AtRiskFlaggable } from './concerns/AtRiskFlaggable';
import { HasCustomFieldValues } from './concerns/HasCustomFieldValues';
import { HasEstimates } from './concerns/HasEstimates';
import { HasExtensionFields } from './concerns/HasExtensionFields';
import { HasRecordPositions } from './concerns/HasRecordPositions';
import { TeamAssignable } from './concerns/TeamAssignable';

interface Requirement
  extends RequirementAttributes,
    TeamAssignable,
    HasCustomFieldValues,
    HasRecordPositions,
    HasExtensionFields,
    HasEstimates,
    AtRiskFlaggable {}

@model
@HasCustomFieldValues
@HasRecordPositions
@HasExtensionFields
@HasEstimates
@TeamAssignable
@AtRiskFlaggable
class Requirement extends ApplicationModel {
  // Start auto-generated
  static typename: 'Requirement' = 'Requirement';
  get typename(): 'Requirement' {
    return 'Requirement';
  }

  static internalTypename: 'Requirement' = 'Requirement';
  get internalTypename(): 'Requirement' {
    return 'Requirement';
  }

  static terminologyName = 'Requirement';

  static inputType = 'RequirementAttributes!';

  static filterType = 'RequirementFilters!';
  static filters = {
    active: 'Boolean',
    id: '[ID!]',
    releaseId: 'ID',
    projectId: 'ID',
    teamId: 'ID',
    iterationId: 'ID',
    programIncrementId: 'ID',
    assignedToUserId: 'ID',
    hasBookmarkPosition: 'HasBookmarkPositionFilter',
    workflowMeaning: '[WorkflowMeaningEnum!]',
  };

  static fields = {
    commentsCount: attr({ readable: true }),
    createdAt: attr({ readable: true }),
    drillInUrl: attr({
      args: {
        publishableId: 'ID',
        publishableType: 'String',
        viewSnapshotId: 'ID',
        dashboardId: 'ID',
        exportType: 'String',
        bookmarkId: 'ID',
      },
      readable: true,
    }),
    durationInTeamWorkflowStatus: attr({ readable: true }),
    epicId: attr({ readable: true }),
    featureId: attr({ readable: true }),
    goalsCount: attr({ readable: true }),
    id: attr({ readable: true }),
    initiativeId: attr({ readable: true }),
    integrationLinksCount: attr({ readable: true }),
    iterationId: attr({ readable: true }),
    manualRiskComment: attr({ writable: true, readable: true }),
    name: attr({ writable: true, readable: true }),
    path: attr({ readable: true }),
    position: attr({ writable: true, readable: true }),
    prioritizedBacklogRank: attr({ readable: true }),
    programIncrementId: attr({ readable: true }),
    projectId: attr({ readable: true }),
    referenceNum: attr({ readable: true }),
    releaseId: attr({ readable: true }),
    releasePhaseId: attr({ readable: true }),
    riskFactors: attr({ readable: true, json: true }),
    riskFactorsCount: attr({ readable: true }),
    tagList: attr({ readable: true }),
    teamId: attr({ readable: true }),
    teamLocation: attr({ readable: true }),
    teamWorkflowStatusId: attr({ readable: true }),
    updatedAt: attr({ readable: true }),
    workflowStatusId: attr({ readable: true }),
    assignedToUser: attr.belongsTo({
      writeShape: ['id', 'name', 'email'],
      readable: true,
      typename: 'User',
    }),
    bestEstimate: attr.belongsTo({ readable: true, typename: 'Estimate' }),
    createdByUser: attr.belongsTo({ readable: true, typename: 'User' }),
    description: attr.belongsTo({
      writable: true,
      readable: true,
      typename: 'Note',
    }),
    epic: attr.belongsTo({ readable: true, typename: 'Epic' }),
    feature: attr.belongsTo({
      writeShape: ['id'],
      readable: true,
      typename: 'Feature',
    }),
    initialEstimate: attr.belongsTo({
      writable: true,
      readable: true,
      typename: 'Estimate',
    }),
    initiative: attr.belongsTo({ readable: true, typename: 'Initiative' }),
    iteration: attr.belongsTo({
      writeShape: ['id'],
      readable: true,
      typename: 'Iteration',
    }),
    originalEstimate: attr.belongsTo({
      writable: true,
      readable: true,
      typename: 'Estimate',
    }),
    permissions: attr.belongsTo({
      readable: true,
      typename: 'ResourcePermissions',
    }),
    programIncrement: attr.belongsTo({
      readable: true,
      typename: 'ProgramIncrement',
    }),
    project: attr.belongsTo({ readable: true, typename: 'Project' }),
    release: attr.belongsTo({ readable: true, typename: 'Release' }),
    releasePhase: attr.belongsTo({ readable: true, typename: 'ReleasePhase' }),
    remainingEstimate: attr.belongsTo({
      writable: true,
      readable: true,
      typename: 'Estimate',
    }),
    researchFolder: attr.belongsTo({
      writeShape: ['id'],
      readable: true,
      typename: 'Page',
    }),
    team: attr.belongsTo({
      writeShape: ['id', 'name', 'referencePrefix'],
      readable: true,
      typename: 'Project',
    }),
    teamWorkflowStatus: attr.belongsTo({
      writeShape: ['id', 'name', 'category'],
      readable: true,
      typename: 'WorkflowStatus',
    }),
    terminology: attr.belongsTo({
      readable: true,
      typename: 'TerminologyItem',
    }),
    workDone: attr.belongsTo({
      writable: true,
      readable: true,
      typename: 'Estimate',
    }),
    workflowStatus: attr.belongsTo({
      writeShape: ['id', 'name', 'category'],
      readable: true,
      typename: 'WorkflowStatus',
    }),
    bookmarksRecordPositions: attr.hasMany({
      args: { filters: 'RecordPositionFilters' },
      readable: true,
    }),
    childRecordLinks: attr.hasMany({
      args: { filters: 'RecordLinkFilters' },
      readable: true,
    }),
    comments: attr.hasMany({ readable: true }),
    customFieldValues: attr.hasMany({
      args: { ids: '[ID!]', keys: '[String!]' },
      readable: true,
    }),
    extensionFields: attr.hasMany({
      args: { filters: 'ExtensionFieldFilters' },
      readable: true,
    }),
    goals: attr.hasMany({ readable: true }),
    integrationLinks: attr.hasMany({ readable: true }),
    parentRecordLinks: attr.hasMany({
      args: { filters: 'RecordLinkFilters' },
      readable: true,
    }),
    tags: attr.hasMany({ readable: true }),
    tasks: attr.hasMany({ readable: true }),
  };
  // End auto-generated

  get container() {
    return this.feature;
  }
}

export default Requirement;
